/**
 * PrivacyPolicyContent - Conteudo da Politica de Privacidade
 * Em conformidade com LGPD (Lei 13.709/2018)
 */

import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Storage as StorageIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  ContactMail as ContactIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

interface PrivacyPolicySectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section: React.FC<PrivacyPolicySectionProps> = ({ title, icon, children }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

interface PrivacyPolicyContentProps {
  companyName?: string;
  lastUpdated?: string;
}

const PrivacyPolicyContent: React.FC<PrivacyPolicyContentProps> = ({
  companyName = 'Clinafy',
  lastUpdated = '11 de dezembro de 2025',
}) => {
  return (
    <Box sx={{ color: 'text.primary' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
        Politica de Privacidade
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Ultima atualizacao: {lastUpdated}
      </Typography>

      <Typography variant="body1" paragraph sx={{ mt: 2 }}>
        A {companyName} esta comprometida com a protecao da sua privacidade e dos seus dados pessoais.
        Esta Politica de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas
        informacoes em conformidade com a Lei Geral de Protecao de Dados (LGPD - Lei 13.709/2018).
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Section 1 - Data Controller */}
      <Section title="1. Controlador dos Dados" icon={<GavelIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          O controlador dos dados pessoais e a <strong>{companyName}</strong>, responsavel pelas
          decisoes referentes ao tratamento dos seus dados pessoais.
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Encarregado de Dados (DPO):</strong> dpo@clinafy.com
          </Typography>
          <Typography variant="body2">
            <strong>Contato:</strong> privacidade@clinafy.com
          </Typography>
        </Box>
      </Section>

      {/* Section 2 - Data Collected */}
      <Section title="2. Dados Coletados" icon={<StorageIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Coletamos os seguintes tipos de dados:
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
          2.1 Dados fornecidos diretamente por voce:
        </Typography>
        <List dense>
          {[
            'Nome completo, email, telefone e endereco',
            'CPF/CNPJ para emissao de documentos fiscais',
            'Dados de login e senha (armazenados de forma criptografada)',
            'Informacoes profissionais (registro profissional, especialidade)',
            'Dados dos seus pacientes/clientes (inseridos por voce)',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
          2.2 Dados coletados automaticamente:
        </Typography>
        <List dense>
          {[
            'Endereco IP e informacoes do navegador',
            'Dados de uso e navegacao na plataforma',
            'Informacoes do dispositivo utilizado',
            'Cookies essenciais para funcionamento',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="info" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Section 3 - Purpose */}
      <Section title="3. Finalidade do Tratamento" icon={<CheckIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Seus dados pessoais sao tratados para as seguintes finalidades:
        </Typography>
        <List dense>
          {[
            'Prestacao dos servicos contratados (gestao de consultorio/clinica)',
            'Comunicacao sobre sua conta, servicos e atualizacoes',
            'Processamento de pagamentos e faturamento',
            'Suporte tecnico e atendimento ao cliente',
            'Cumprimento de obrigacoes legais e regulatorias',
            'Melhoria continua da plataforma e experiencia do usuario',
            'Prevencao de fraudes e seguranca da plataforma',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Section 4 - Legal Basis */}
      <Section title="4. Base Legal" icon={<GavelIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          O tratamento dos seus dados esta fundamentado nas seguintes bases legais da LGPD:
        </Typography>
        <List dense>
          {[
            'Art. 7, I - Consentimento do titular',
            'Art. 7, II - Cumprimento de obrigacao legal ou regulatoria',
            'Art. 7, V - Execucao de contrato ou procedimentos preliminares',
            'Art. 7, IX - Interesses legitimos do controlador',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><GavelIcon fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Section 5 - Data Sharing */}
      <Section title="5. Compartilhamento de Dados" icon={<ShareIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Seus dados podem ser compartilhados nas seguintes situacoes:
        </Typography>
        <List dense>
          {[
            'Prestadores de servicos essenciais (hospedagem, pagamentos, email)',
            'Autoridades governamentais quando exigido por lei',
            'Parceiros comerciais (apenas com seu consentimento expresso)',
            'Em caso de fusao, aquisicao ou reorganizacao societaria',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><ShareIcon fontSize="small" color="action" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, mt: 2, border: '1px solid', borderColor: 'warning.200' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Importante: Nunca vendemos seus dados pessoais para terceiros.
          </Typography>
        </Box>
      </Section>

      {/* Section 6 - Security */}
      <Section title="6. Seguranca dos Dados" icon={<SecurityIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Implementamos medidas tecnicas e organizacionais para proteger seus dados:
        </Typography>
        <List dense>
          {[
            'Criptografia de dados em transito (HTTPS/TLS)',
            'Criptografia de dados em repouso',
            'Autenticacao segura com hash de senhas (PBKDF2)',
            'Controle de acesso baseado em funcoes (RBAC)',
            'Backups automaticos e plano de recuperacao',
            'Monitoramento de seguranca 24/7',
            'Testes periodicos de vulnerabilidade',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><SecurityIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Section 7 - Retention */}
      <Section title="7. Retencao de Dados" icon={<StorageIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Seus dados serao mantidos pelo tempo necessario para:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Dados de conta"
              secondary="Enquanto a conta estiver ativa + 5 anos apos encerramento"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Dados de pacientes/clientes"
              secondary="Conforme exigencias legais da sua profissao (ex: 5 anos conforme regulamentacoes profissionais)"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Dados fiscais"
              secondary="5 anos conforme legislacao tributaria"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Logs de acesso"
              secondary="6 meses conforme Marco Civil da Internet"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            />
          </ListItem>
        </List>
      </Section>

      {/* Section 8 - Your Rights */}
      <Section title="8. Seus Direitos" icon={<ContactIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Conforme a LGPD, voce tem direito a:
        </Typography>
        <List dense>
          {[
            'Confirmacao da existencia de tratamento',
            'Acesso aos seus dados pessoais',
            'Correcao de dados incompletos, inexatos ou desatualizados',
            'Anonimizacao, bloqueio ou eliminacao de dados desnecessarios',
            'Portabilidade dos dados a outro fornecedor',
            'Eliminacao dos dados tratados com consentimento',
            'Informacao sobre compartilhamento com terceiros',
            'Revogacao do consentimento a qualquer momento',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, mt: 2 }}>
          <Typography variant="body2">
            Para exercer seus direitos, entre em contato: <strong>privacidade@clinafy.com</strong>
          </Typography>
        </Box>
      </Section>

      {/* Section 9 - Cookies */}
      <Section title="9. Cookies" icon={<StorageIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Utilizamos cookies essenciais para:
        </Typography>
        <List dense>
          {[
            'Manter sua sessao de login ativa',
            'Lembrar suas preferencias de uso',
            'Garantir a seguranca da plataforma',
            'Analisar uso anonimo para melhorias',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="action" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Section 10 - Changes */}
      <Section title="10. Alteracoes nesta Politica" icon={<GavelIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Podemos atualizar esta Politica de Privacidade periodicamente. Alteracoes significativas
          serao comunicadas por email ou notificacao na plataforma. Recomendamos revisar esta
          pagina regularmente.
        </Typography>
      </Section>

      {/* Section 11 - Contact */}
      <Section title="11. Contato" icon={<ContactIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Para duvidas sobre esta politica ou exercicio dos seus direitos:
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Email:</strong> privacidade@clinafy.com</Typography>
          <Typography variant="body2"><strong>DPO:</strong> dpo@clinafy.com</Typography>
        </Box>
      </Section>

      {/* Section 12 - Deletion */}
      <Section title="12. Exclusao de Conta" icon={<DeleteIcon color="primary" />}>
        <Typography variant="body2" paragraph>
          Para solicitar a exclusao da sua conta e dados pessoais:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="1. Acesse Configuracoes > Minha Conta > Excluir Conta"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2. Ou envie email para: exclusao@clinafy.com"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </List>

        <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1, mt: 2, border: '1px solid', borderColor: 'error.200' }}>
          <Typography variant="body2" color="error.dark">
            <strong>Atencao:</strong> Alguns dados podem ser retidos por obrigacao legal mesmo apos exclusao da conta.
          </Typography>
        </Box>
      </Section>

      <Divider sx={{ my: 3 }} />

      {/* Footer */}
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Ao utilizar a plataforma {companyName}, voce declara ter lido e compreendido esta
        Politica de Privacidade e concorda com o tratamento dos seus dados conforme descrito.
      </Typography>
    </Box>
  );
};

export default PrivacyPolicyContent;
